import { FC, useEffect, useState } from 'react';
import { CircularProgress, Box } from '@mui/material';
import useSubmoduleFilterValues from '@hooks/api/useSubmoduleFilterValues';
import { SubmoduleFilterMap } from '@stores/Modules/types/modules';
import { titles } from '@utils/translations/en';
import { SidepanelFooter, SidepanelHeader, SidepanelLayout } from '../Sidepanel';
import FilterGroupsList, {
  buildFilterGroupSelection,
  FilterGroupSelectionItem,
} from './FilterGroupsList';
import { mapSubmoduleFiltersToFilterItems } from '../../utils/utils';

export interface SelectCustomAppsSidepanelProps {
  open: boolean;
  moduleName: string;
  moduleId: string;
  subModuleId: string;
  selectedFilters: string[];
  filterMap?: SubmoduleFilterMap | null;
  onRefreshFilters?: () => Promise<void>;
  onSave: (selection: FilterGroupSelectionItem[]) => void;
  onClose: () => void;
  isViewOnly?: boolean;
}

const SelectCustomAppsSidepanel: FC<SelectCustomAppsSidepanelProps> = ({
  open,
  moduleName,
  moduleId,
  subModuleId,
  selectedFilters,
  filterMap,
  onRefreshFilters,
  onSave,
  onClose,
  isViewOnly = false,
}) => {
  const { addFilterValue, isLoading: isAddingGame } = useSubmoduleFilterValues();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const filterGroupListMap = filterMap ? mapSubmoduleFiltersToFilterItems(filterMap) : null;

  useEffect(() => {
    if (!open || !subModuleId) return;
    setSelectedIds(selectedFilters);
  }, [open, subModuleId, selectedFilters]);

  const existingGameIds = filterMap
    ? (filterMap['game_id'] ?? Object.values(filterMap)[0] ?? []).map((f) => f.filterValue)
    : [];

  const existingClientCodes = filterMap
    ? (filterMap['client_code'] ?? []).map((f) => f.filterValue)
    : [];

  const handleAddGame = async (gameId: string): Promise<boolean> => {
    if (!gameId || !filterMap) return false;
    const gameIdGroup = filterMap['game_id'] ?? Object.values(filterMap)[0] ?? [];
    const filterId = gameIdGroup[0]?.submoduleFilterId;
    if (!filterId) return false;

    const res = await addFilterValue({
      moduleId,
      submoduleId: subModuleId,
      filterId,
      values: [gameId],
    });

    if (res?.code) {
      if (onRefreshFilters) {
        await onRefreshFilters();
      }
    }

    return Boolean(res?.code);
  };

  const handleAddClientCode = async (clientCode: string): Promise<boolean> => {
    if (!clientCode || !filterMap) return false;
    const clientCodeGroup = filterMap['client_code'] ?? Object.values(filterMap)[0] ?? [];
    const filterId = clientCodeGroup[0]?.submoduleFilterId;

    if (!filterId) return false;

    const res = await addFilterValue({
      moduleId,
      submoduleId: subModuleId,
      filterId,
      values: [clientCode],
    });

    if (res?.code) {
      if (onRefreshFilters) {
        await onRefreshFilters();
      }
    }

    return Boolean(res?.code);
  };

  const handleSave = () => {
    const selection = filterGroupListMap
      ? buildFilterGroupSelection(filterGroupListMap, selectedIds)
      : [];
    onSave(selection);
    onClose();
  };

  const showSpinner = isAddingGame || filterMap == null;

  return (
    <SidepanelLayout open={open} onClose={onClose}>
      <SidepanelHeader
        title={titles.SELECT_CUSTOM_APPS}
        onClose={onClose}
        subtitleLabel="Sub Module"
        subtitle={moduleName}
      />

      {showSpinner ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <FilterGroupsList
          filterMap={filterGroupListMap || {}}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          addGame={handleAddGame}
          addClientCode={handleAddClientCode}
          isAddingGame={isAddingGame}
          existingGameIds={existingGameIds}
          existingClientCodes={existingClientCodes}
          isViewOnly={isViewOnly}
        />
      )}

      <SidepanelFooter onSave={handleSave} onCancel={onClose} isSaveDisabled={isViewOnly} />
    </SidepanelLayout>
  );
};

export default SelectCustomAppsSidepanel;
